import React, { useState } from 'react'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import ConfirmDialogShell from '../../tenant/components/billing/ConfirmDialogShell'
import Button from '../../../../components/Form/Button'

export interface ArchiveOnboardingDialogProps {
  isOpen: boolean
  /** Display label for the record (business name preferred). */
  businessName: string
  /**
   * True when the record has progressed past "Registration invitation generated" (TESA accepted,
   * supplement accepted, payment accepted, invite consumed, etc.). Surfaces an inline warning so
   * the admin sees that meaningful onboarding work will be hidden by archiving.
   */
  progressBeyondInvite: boolean
  onCancel: () => void
  onConfirm: () => Promise<void> | void
}

/**
 * Confirmation dialog for soft-archiving a tenant onboarding record. Archive is reversible
 * (`Show archived` filter on the list view + Unarchive button), so this is a simple Yes/No
 * confirmation rather than a typed-confirmation modal.
 */
export const ArchiveOnboardingDialog: React.FC<ArchiveOnboardingDialogProps> = ({
  isOpen,
  businessName,
  progressBeyondInvite,
  onCancel,
  onConfirm,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleConfirm = async () => {
    setIsSubmitting(true)
    try {
      await onConfirm()
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <ConfirmDialogShell isOpen={isOpen}>
      <div className="space-y-4">
        <div role="heading" aria-level={3} className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          Archive onboarding record
        </div>
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Archive the registration record for{' '}
          <span className="font-medium text-slate-900 dark:text-slate-100">{businessName}</span>? It will be hidden from the onboarding list. You can restore
          it later from the list&apos;s &quot;Show archived&quot; view.
        </p>

        {progressBeyondInvite && (
          <div className="flex gap-2 rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
            <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
            <div>
              <div className="font-medium">Are you sure? This record appears to have in-progress activity past the invitation generated stage.</div>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button color="slate" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button color="red" onClick={handleConfirm} disabled={isSubmitting} isSaving={isSubmitting}>
            Archive
          </Button>
        </div>
      </div>
    </ConfirmDialogShell>
  )
}

export default ArchiveOnboardingDialog
